import { useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { provideHooks } from "redial";
import ProductListingPage from "../../components/ProductListingPage";
import ProductListSkeleton from "../../components/Results/ProductListSkeleton";
import { withTranslation } from "../../components/translation";
import { useBreadcrumbUpdates } from "../../components/hooks";
import { createSearchBreadcrumbs } from "../../helpers/createSearchBreadcrumbs";
import { mapBreadcrumbs } from "../../state/modules/category/mapBreadcrumbs";
import { useUpdateRefineParameters } from "../../components/hooks/useUpdateRefineParameters";
import {
  fetchSearch,
  getItemCount,
  hasFacets,
  getPaginationData,
  getIsNewSearch,
  getSearchTerm,
  sendPlacementOnLoadBeacon
} from "../../state/modules/search";
import { setPageLoading, setIsNewPage } from "../../state/modules/page/actions";
import { analyticsPageLoad } from "../../state/modules/analytics/actions";
import { getError } from "../../state/modules/error";
import { getStoreUrl } from "../../state/modules/config/selectors";
import { getIsLoading } from "../../state/modules/page/selectors";
import {
  clearCategoryDetails,
  clearCuratedCategoryTreatments
} from "../../state/modules/category/actions/";

const isValidSearch = location => {
  const searchTerm =
    new URLSearchParams(location?.search).get("q")?.trim().length > 0;

  return searchTerm;
};

export const hooks = {
  fetch: ({ dispatch, location, store, uuid, xSiteOrigin, isNewPage }) => {
    const state = store.getState();
    if (isValidSearch(location)) {
      if (!getIsNewSearch(state)) {
        return;
      }

      return dispatch(fetchSearch(uuid, xSiteOrigin, isNewPage, location))
        .then(() => {
          dispatch(sendPlacementOnLoadBeacon());
          if (isNewPage) {
            dispatch(clearCategoryDetails());
            dispatch(clearCuratedCategoryTreatments());
            dispatch(analyticsPageLoad());
            dispatch(setIsNewPage(false));
          }
        })
        .catch(error => {
          if (typeof window === "undefined") {
            throw error;
          }
        })
        .finally(() => {
          dispatch(setPageLoading(false));
        });
    }
  }
};

export const SearchPage = ({ formatTranslation, location }) => {
  const hasFacetsValue = useSelector(hasFacets);
  const itemCount = useSelector(getItemCount);
  const isLoading = useSelector(getIsLoading);
  const paginationData = useSelector(getPaginationData);
  const searchTerm = useSelector(getSearchTerm);
  const error = useSelector(getError);
  const storeUrl = useSelector(getStoreUrl);

  const page = paginationData?.page;

  if (isLoading) {
    return <ProductListSkeleton />;
  }

  useBreadcrumbUpdates(
    mapBreadcrumbs(
      createSearchBreadcrumbs({
        homeTranslation: formatTranslation("plp_web_breadcrumbs_home"),
        homeLink: storeUrl,
        searchTermTranslation: formatTranslation(
          "plp_web_breadcrumbs_search_results_for"
        ),
        searchTerm
      })
    )
  );

  useUpdateRefineParameters({ searchTerm });

  useEffect(() => {
    if (searchTerm) {
      const { initial, max } = page;

      document.title = formatTranslation("plp_web_search_page_title", {
        searchTerm,
        currentPage: initial,
        maxPage: max
      });
    }
  }, [searchTerm]);

  return (
    <ProductListingPage
      hasFacets={hasFacetsValue}
      itemCount={itemCount}
      paginationData={paginationData}
      error={error}
      searchTerm={searchTerm}
      location={location}
    />
  );
};

SearchPage.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  location: PropTypes.object
};

export default provideHooks(hooks)(withTranslation(SearchPage));
